import { lazy, Suspense, useRef } from 'react';
import { useReportColors } from '../palette/ReportColorPaletteContext';
import { useChartFit } from './shared';
import { useElementSize } from './useElementSize';
import type { BuiltinChartRequest } from './viz/request';

// The Vega stack is heavy (~1.5 MB minified); load it only when a canvas
// actually draws a chart.
const BuiltinChartInner = lazy(() =>
  import('./viz/BuiltinChartInner').then((m) => ({
    default: m.BuiltinChartInner,
  })),
);

/** The height a chart takes before its wrapper is measured: a 6-row cell's body. */
const UNMEASURED_HEIGHT = 240;

/**
 * A builtin chart drawn with `@erp/viz` in the room its body has: measured,
 * so the chart's axes, legend and labels fit inside it, at the detail its
 * cell allows (`useChartFit`), in the Report's colors. Tooltips are
 * vega-tooltip's, on <body>, so a clipping cell never cuts them off.
 */
export function BuiltinChart({ request }: { request: BuiltinChartRequest }) {
  const ref = useRef<HTMLDivElement>(null);
  const measured = useElementSize(ref);
  const fit = useChartFit();
  const palette = useReportColors();
  const height =
    measured.height > 0 ? measured.height : fit.height || UNMEASURED_HEIGHT;
  return (
    // The chart sits out of flow, so its own size never feeds back into the
    // size it is measured at.
    <div ref={ref} className="relative min-h-0 min-w-0 flex-1">
      <div className="absolute inset-0">
        <Suspense fallback={null}>
          <BuiltinChartInner
            request={request}
            height={height}
            width={measured.width || fit.width}
            detail={fit.detail}
            palette={palette}
          />
        </Suspense>
      </div>
    </div>
  );
}
